import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ERROR_REENABLE_DELAY = 1000;

/**
 * Product form component — aligns with Horizon's ProductFormComponent.
 *
 * Intercepts product-form submissions and routes to the Shopify
 * AJAX Cart API using Horizon's proven patterns:
 *
 * - Single-item: FormData as multipart/form-data (browser sets Content-Type
 *   boundary), with X-Requested-With header so Shopify recognizes AJAX.
 *   Sections param triggers bundled section rendering.
 *
 * - Batch (cross-sell): JSON { items: [...], sections: "..." } POST.
 *
 * Cart surfaces self-morph from response.sections via CartItemsComponent.
 * Cart icon badge uses additive logic (itemCount = items added, not total).
 */
export class ProductFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * Collects section IDs from all cart-items-component elements on the page.
   * Matches Horizon's pattern: collects from every cart-items-component.
   * @returns {string} Comma-separated section IDs
   */
  #collectCartItemSectionIds() {
    const ids = [];
    const components = document.querySelectorAll('cart-items-component');
    for (const el of components) {
      if (el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    }
    return ids.join(',');
  }

  #handleSubmit = (event) => {
    event.preventDefault();

    /* CN market: online ordering is disabled — bounce to JD/Tmall link if present */
    if (document.documentElement.hasAttribute('data-market-cn')) {
      const external = document.querySelector('.purchase-area__external-links a[href]');
      if (external && external.getAttribute('href')) {
        window.location.href = external.getAttribute('href');
      }
      return;
    }

    const form = event.target.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const mainId = formData.get('id');
    const mainQty = Number(formData.get('quantity')) || 1;
    if (!mainId) return;

    /* Collect cross-sell items */
    const section = this.closest('[data-section-id]');
    const checkedCbs = section
      ? section.querySelectorAll('[data-cross-sell-checkbox]:checked')
      : document.querySelectorAll('[data-cross-sell-checkbox]:checked');

    const crossSellItems = [];

    checkedCbs.forEach((cb) => {
      const item = cb.closest('.cross-sell__item');
      if (!item) return;

      let variantId = null;
      const selectedOption = item.querySelector(
        '.cross-sell__variant-option.is-selected'
      );
      if (selectedOption) {
        variantId = selectedOption.dataset.variantId;
      } else {
        variantId = item.dataset.crossSellDefaultVariantId;
      }

      const qtyEl = item.querySelector('.cross-sell__qty-value');
      const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;

      if (variantId) {
        crossSellItems.push({ id: Number(variantId), quantity: qty });
      }
    });

    const hasCrossSell = crossSellItems.length > 0;

    this.#disableButtons(form);

    const root = window.Shopify?.routes?.root;
    if (!root) {
      this.#onAddError(form, new Error('Missing window.Shopify.routes.root'));
      return;
    }

    if (hasCrossSell) {
      /* Batch path: JSON { items, sections } — aligns with Horizon's
         #processBatchAddToCart */
      const items = [
        { id: Number(mainId), quantity: mainQty },
        ...crossSellItems,
      ];

      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      })
        .then((response) => response.json())
        .then((responseJson) => {
          this.#onAddSuccess(form, responseJson, items);
        })
        .catch((error) => {
          this.#onAddError(form, error);
        });
      return;
    }

    /* Single-item path: FormData as multipart/form-data — aligns with
       Horizon's #processAddToCart.
       - X-Requested-With tells Shopify this is AJAX (critical!)
       - No Content-Type: browser sets multipart/form-data boundary
       - sections appended to FormData for bundled section rendering */
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((responseJson) => {
        const items = [{ id: Number(mainId), quantity: mainQty }];
        this.#onAddSuccess(form, responseJson, items);
      })
      .catch((error) => {
        this.#onAddError(form, error);
      });
  };

  /**
   * Handles a successful /cart/add.js JSON response.
   * Dispatches CartAddEvent with sections HTML for cart surface morphing.
   *
   * @param {HTMLFormElement} form
   * @param {Object} responseJson - Parsed /cart/add.js response
   * @param {Array<{id: number, quantity: number}>} items - Items that were added
   */
  #onAddSuccess(form, responseJson, items) {
    /* addedCount: how many we tried to add (NOT total cart count).
       CartIconComponent uses additive logic for product-form-component
       source, so this must be the increment, not the absolute total. */
    const addedCount = items.reduce((sum, it) => sum + it.quantity, 0);

    if (responseJson.status) {
      /* Server error (e.g. 422) — aligns with Horizon error handling */
      this.dispatchEvent(
        new CartErrorEvent(
          form.getAttribute('id') || '',
          responseJson.message ||
            responseJson.description,
          responseJson.description,
          responseJson.errors
        )
      );

      /* No cart:update on failure: /cart/add.js is atomic, so nothing was
         added — dispatching with the attempted qty would inflate the badge
         and auto-open the drawer. The CartErrorEvent above surfaces it. */

      this.#enableButtons(form, ERROR_REENABLE_DELAY);
      return;
    }

    /* Re-enable buttons immediately */
    this.#enableButtons(form, 0);

    /* Dispatch cart:update on document.
       CartItemsComponent self-morphs from event.detail.data.sections
       when sections HTML is present (bundled section rendering).
       CartIconComponent updates badge using additive logic.
       CartDrawerComponent auto-opens when source === 'product-form-component'. */
    document.dispatchEvent(
      new CartAddEvent(responseJson, this.dataset.productId, {
        source: 'product-form-component',
        itemCount: addedCount,
        productId: this.dataset.productId,
        sections: responseJson.sections || null,
      })
    );
  }

  /**
   * Handles fetch-level errors (network failures, etc.).
   * @param {HTMLFormElement} form
   * @param {Error} error
   */
  #onAddError(form, error) {
    console.error('[product-form]', error);

    this.dispatchEvent(
      new CartErrorEvent(
        form.getAttribute('id') || '',
        ''
      )
    );

    this.#enableButtons(form, ERROR_REENABLE_DELAY);
  }

  #disableButtons(form) {
    const atcBtn = form.querySelector('.purchase-area__atc');
    if (atcBtn) atcBtn.setAttribute('disabled', '');

    const section = this.closest('[data-section-id]');
    const stickyBtn = section
      ? section.querySelector('.sticky-add-to-cart__button')
      : document.querySelector('.sticky-add-to-cart__button');
    if (stickyBtn) stickyBtn.setAttribute('disabled', '');
  }

  #enableButtons(form, delay) {
    const reenable = () => {
      const atcBtn = form.querySelector('.purchase-area__atc');
      if (atcBtn) atcBtn.removeAttribute('disabled');

      const section = this.closest('[data-section-id]');
      const stickyBtn = section
        ? section.querySelector('.sticky-add-to-cart__button')
        : document.querySelector('.sticky-add-to-cart__button');
      if (stickyBtn) stickyBtn.removeAttribute('disabled');
    };

    if (delay > 0) {
      setTimeout(reenable, delay);
    } else {
      reenable();
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
